import type { MetricDim, TileResult } from "@csbi/contracts"
import { UNCATEGORISED } from "@csbi/viz-core"
import type { TileSpec } from "./hooks/use-tile-query.js"

/**
 * ⚠ **Only a chart split by a dimension can be drilled, and that is not a limitation to work around.** A metric
 * that declares no dimensions is a bare count: `users_added` is the number 7, and there is no list of seven
 * people anywhere in this system — the count was taken in srv, against records this product never receives. A
 * drill affordance on such a chart would promise something that cannot exist, so those charts stay un-clickable
 * rather than opening an empty panel. Of the catalog, 29 metrics can be drilled this way and 52 never can.
 */

export function drillableDim(spec: TileSpec): MetricDim | null {
  const split = spec.splitBy ?? ["period"]
  if (split.length !== 1) return null
  const [only] = split
  return only && only !== "period" ? (only as MetricDim) : null
}

export function drillSpecFor(spec: TileSpec, dim: MetricDim): TileSpec {
  return {
    tileKey: `${spec.tileKey}.drill.${dim}`,
    metrics: spec.metrics,
    splitBy: ["period", dim],
  }
}

export function filterToCategory(result: TileResult, dim: string, category: string): TileResult {
  const column = result.columns.find((c) => c.role === "dimension" && c.key.endsWith(`.${dim}`))
  if (!column) return { ...result, rows: [] }

  const wanted = category === UNCATEGORISED ? "" : category
  const rows = result.rows.filter((r) => String(r[column.key] ?? "") === wanted)
  return {
    ...result,
    columns: result.columns.filter((c) => c.key !== column.key),
    rows,
  }
}
